import * as React from "react";
import { downloadAgreementVersion, type VersionSummary } from "../../agreement";
import { VersionActionBtn } from "./VersionActionBtn";
import { IconDownload, IconLink } from "./VersionIcons";
import { sharePointUrlForVersion } from "./versionHistoryUtils";
import { showPluginToast } from "../shell/PluginToast";

export type VersionRowActionsProps = {
  label: string;
  version: VersionSummary;
  compact?: boolean;
  promoting?: boolean;
  onPromote?: () => void;
};

export function openSharePointForVersion(label: string, version: VersionSummary) {
  const url = sharePointUrlForVersion(version);
  if (!url) {
    showPluginToast(`No SharePoint link mapped for ${label}.`);
    return;
  }
  const win = window.open(url, "_blank", "noopener,noreferrer");
  if (!win) {
    showPluginToast("Popup blocked — allow popups to open SharePoint.");
  }
}

export const VersionRowActions: React.FC<VersionRowActionsProps> = ({
  label,
  version,
  compact,
  promoting,
  onPromote,
}) => {
  const spUrl = sharePointUrlForVersion(version);
  const iconSize = compact ? 12 : 13;
  const showPromote = Boolean(onPromote && version.is_promotable && !version.is_promoted);
  const [downloading, setDownloading] = React.useState(false);

  const handleDownload = React.useCallback(async () => {
    const versionId = (version.id || version.version_id || "").trim();
    if (!versionId) {
      showPluginToast(`No document id for ${label}.`);
      return;
    }
    setDownloading(true);
    try {
      await downloadAgreementVersion(versionId, {
        suggestedBaseName: label.replace(/\s+/g, "-"),
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      showPluginToast(msg || `Could not download ${label}.`);
    } finally {
      setDownloading(false);
    }
  }, [label, version.id, version.version_id]);

  return (
    <span className="versions-row__actions">
      {showPromote ? (
        <button
          type="button"
          className={`versions-promote-btn${compact ? " versions-promote-btn--compact" : ""}`}
          disabled={promoting}
          title={`Promote ${label} to the next major version`}
          onClick={(e) => {
            e.stopPropagation();
            onPromote?.();
          }}
        >
          {promoting ? "…" : "Promote"}
        </button>
      ) : null}
      {spUrl ? (
        <VersionActionBtn
          title={`Open ${label} in SharePoint`}
          compact={compact}
          onClick={() => openSharePointForVersion(label, version)}
        >
          <IconLink size={iconSize} />
        </VersionActionBtn>
      ) : null}
      <VersionActionBtn
        title={downloading ? `Downloading ${label}…` : `Download ${label}`}
        compact={compact}
        disabled={downloading}
        onClick={() => void handleDownload()}
      >
        <IconDownload size={iconSize} />
      </VersionActionBtn>
    </span>
  );
};
